import * as CryptoJS from 'crypto-js';
import base64js from 'base64-js';

// Generate a secure random key for encryption
export const generateEncryptionKey = (): string => {
  const keyBytes = new Uint8Array(32); // 256 bits
  crypto.getRandomValues(keyBytes);
  let base64String = base64js.fromByteArray(keyBytes);
  
  // Ensure proper base64 padding
  while (base64String.length % 4 !== 0) {
    base64String += '=';
  }
  
  return base64String;
};

export const encryptContent = async (content: string, key: string): Promise<string> => {
  try {
    if (!content) {
      throw new Error('Content is required for encryption');
    }

    if (!key) {
      throw new Error('Encryption key is required');
    }

    // Convert content to WordArray
    const contentWordArray = CryptoJS.enc.Utf8.parse(content);
    
    // Generate a random salt
    const salt = CryptoJS.lib.WordArray.random(128 / 8);
    
    // Derive key using PBKDF2
    const derivedKey = CryptoJS.PBKDF2(key.trim(), salt, {
      keySize: 256 / 32,
      iterations: 10000,
      hasher: CryptoJS.algo.SHA256
    });

    // Generate random IV
    const iv = CryptoJS.lib.WordArray.random(128 / 8);

    // Create cipher text with AES
    const encrypted = CryptoJS.AES.encrypt(contentWordArray, derivedKey, {
      iv: iv,
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.Pkcs7
    });

    // Get the cipher text as WordArray
    const ciphertext = encrypted.ciphertext;

    // Combine version, salt, IV, and encrypted data
    const version = "02"; // Updated version for new encryption method
    const saltHex = salt.toString(CryptoJS.enc.Hex);
    const ivHex = iv.toString(CryptoJS.enc.Hex);
    const ciphertextHex = ciphertext.toString(CryptoJS.enc.Hex);
    
    return `${version}${saltHex}${ivHex}${ciphertextHex}`;
  } catch (error) {
    console.error('Encryption error:', error);
    throw new Error('Failed to encrypt content');
  }
};

export const decryptContent = async (encrypted: string, key: string): Promise<string> => {
  try {
    if (!encrypted) {
      throw new Error('Encrypted content is required for decryption');
    }

    if (!key) {
      throw new Error('Decryption key is required');
    }

    // Check minimum length and version
    if (encrypted.length < 66) { // 2 (version) + 32 (salt) + 32 (iv)
      throw new Error('Invalid encrypted content format');
    }

    const version = encrypted.substring(0, 2);
    
    // Handle different versions
    if (version === "01") {
      return decryptLegacyContent(encrypted, key);
    } else if (version === "02") {
      return decryptCurrentContent(encrypted, key);
    } else {
      throw new Error('Unsupported encryption version');
    }
  } catch (error) {
    console.error('Decryption error:', error);
    if (error instanceof Error && error.message === 'Invalid password') {
      throw error;
    }
    throw new Error('Failed to decrypt content');
  }
};

const decryptLegacyContent = async (encrypted: string, key: string): Promise<string> => {
  const saltHex = encrypted.substring(2, 34);
  const ivHex = encrypted.substring(34, 66);
  const ciphertextHex = encrypted.substring(66);

  // Convert hex strings to WordArrays
  const salt = CryptoJS.enc.Hex.parse(saltHex);
  const iv = CryptoJS.enc.Hex.parse(ivHex);
  const ciphertext = CryptoJS.enc.Hex.parse(ciphertextHex);

  // Derive key using PBKDF2
  const derivedKey = CryptoJS.PBKDF2(key.trim(), salt, {
    keySize: 256 / 32,
    iterations: 10000,
    hasher: CryptoJS.algo.SHA256
  });

  // Create CipherParams object
  const cipherParams = CryptoJS.lib.CipherParams.create({
    ciphertext: ciphertext,
    salt: salt,
    iv: iv
  });

  try {
    // Decrypt data
    const decrypted = CryptoJS.AES.decrypt(cipherParams, derivedKey, {
      iv: iv,
      padding: CryptoJS.pad.Pkcs7,
      mode: CryptoJS.mode.CBC
    });

    const decryptedText = decrypted.toString(CryptoJS.enc.Utf8);
    if (!decryptedText) {
      throw new Error('Invalid password');
    }

    return decryptedText;
  } catch (error) {
    throw new Error('Invalid password');
  }
};

const decryptCurrentContent = async (encrypted: string, key: string): Promise<string> => {
  const saltHex = encrypted.substring(2, 34);
  const ivHex = encrypted.substring(34, 66);
  const ciphertextHex = encrypted.substring(66);

  // Convert hex strings to WordArrays
  const salt = CryptoJS.enc.Hex.parse(saltHex);
  const iv = CryptoJS.enc.Hex.parse(ivHex);
  const ciphertext = CryptoJS.enc.Hex.parse(ciphertextHex);

  // Derive key using PBKDF2 with exact password
  const derivedKey = CryptoJS.PBKDF2(key.trim(), salt, {
    keySize: 256 / 32,
    iterations: 10000,
    hasher: CryptoJS.algo.SHA256
  });

  // Create CipherParams object
  const cipherParams = CryptoJS.lib.CipherParams.create({
    ciphertext: ciphertext,
    salt: salt,
    iv: iv
  });

  try {
    // Decrypt data
    const decrypted = CryptoJS.AES.decrypt(cipherParams, derivedKey, {
      iv: iv,
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.Pkcs7
    });

    const decryptedText = decrypted.toString(CryptoJS.enc.Utf8);
    if (!decryptedText) {
      throw new Error('Invalid password');
    }

    return decryptedText;
  } catch (error) {
    throw new Error('Invalid password');
  }
};